<template>
  <v-navigation-drawer
    v-model="drawer"
    app
    elevation="10"
    :rail-width="!mobile ? 75 : 250"
    :permanent="!mobile"
    :temporary="!mdAndUp"
    :rail="rail"
    class="admin-panel-side-bar">
    <div class="py-2 px-4 w-100 ga-1 d-flex align-center position-fixed top-0 bg-primary logo-container">
      <VImg width="60" :src="appConfig.logoPath" alt="logo" style="max-width: 35px; height: 35px" />
      <h2 v-if="(mobile && drawer) || (!mobile && !rail)" class="text-header font-weight-medium overflow-ellipsis" style="white-space: nowrap">
        {{ appConfig.name }}
      </h2>
    </div>

    <v-list
      ref="navList"
      :nav="true"
      :class="rail ? 'rail-list' : null"
      style="margin-top: 54px; height: calc(100vh - 54px)"
      class="px-3 overflow-y-auto">
      <div v-for="(item, key) in navItems" :key="'item_' + key" class="mb-1">
        <div v-if="(mobile || !rail) && item.heading">
          <v-list-subheader class="">{{ getTitle(item.heading) }}</v-list-subheader>
        </div>
        <v-list-item
          v-if="item.to"
          :to="{ name: item.to == '/' ? 'index' : item.to }"
          class="menu"
          :class="isActive(item.to) ? 'bg-primary elevation-12' : ''"
          link>
          <v-list-item-title class="menu-item">
            <div class="flex menu-text" :class="{ 'text-header': isActive(item.to), translate: !rail }">
              <VTooltip location="top">
                <template #activator="{ props: slotProps }">
                  <v-icon :icon="item.icon" :size="rail && !mobile ? 'x-large' : 'small'" v-bind="slotProps" />
                </template>
                <span>{{ getTitle(item.title) }}</span>
              </VTooltip>
              <span v-if="(mobile && drawer) || (!mobile && !rail)" class="text-body-2 font-weight-medium">{{ getTitle(item.title) }}</span>
            </div>
          </v-list-item-title>
        </v-list-item>
      </div>
    </v-list>
  </v-navigation-drawer>
</template>

<script setup>
import { appConfig } from '@appConfig';
import { useRouter } from 'vue-router';
import navItems from '@/router/navItems';
import { useDisplay } from 'vuetify';
import i18n from '@/plugins/i18n';
import { onMounted, nextTick, ref, watch } from 'vue';

const { t } = i18n.global;
const { mobile, mdAndUp } = useDisplay();

const navList = ref(null);

const props = defineProps({
  rail: { type: Boolean, required: true, default: false },
});
const drawer = defineModel('drawer', {
  type: Boolean,
  default: false,
});
const emit = defineEmits(['update:modelValue']);

const router = useRouter();

function isActive(path) {
  const currentPaths = router.currentRoute.value.path.split('/');
  let currentPath = currentPaths[0] || currentPaths[1] || '/';
  if (currentPath == 'create-order') {
    currentPath = 'order';
  }
  if (currentPath == 'contactus') {
    currentPath = 'contact-us';
  }
  return currentPath === path || `${currentPath}s` === path;
}

function getTitle(title) {
  return t(title?.replace('-id-catchall', '-details').replace(/-([a-z])/g, (_, char) => char.toUpperCase()));
}

function scrollToActive() {
  nextTick(() => {
    const activeEl = navList.value?.$el?.querySelector('.bg-primary');
    if (activeEl) {
      activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
}

watch(
  () => router.currentRoute.value.fullPath,
  () => scrollToActive(),
);
onMounted(() => scrollToActive());
</script>

<style scoped>
.logo-container {
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.25);
}

.heading {
  background-color: rgba(var(--v-theme-primary), 0.6);
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.menu-text {
  width: 100%;
  transition: transform 0.3s ease;
  display: flex;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.3s ease;
  align-items: center;
  gap: 8px;
}

.menu:hover .menu-text {
  transform: none; /* Smooth move to the right */
}

.v-locale--is-rtl .menu:hover .menu-text.translate {
  transform: translateX(-8px); /* Smooth move to the right */
}
.v-locale--is-ltr .menu:hover .menu-text.translate {
  transform: translateX(8px); /* Smooth move to the right */
}
</style>
